import axios from "axios"
import { getBaseEndpoint } from "../../../features/endpoints/endpointManager"
import { practiceSlice } from "../model"
import { LiabilityInsurance, Practice } from "../schema"
import { practiceDataStatusUpdated } from "./loader"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { LiabilityStatusMap } from "../../CMS/schema"
import {
  downloadFileBlob,
  getFilenameFromContentDisposition,
} from "../../../features/shared/utils"
import { cloneDeep } from "lodash"
import { AppThunk, RootState } from "../../../reduxStore/store"
import {
  bannersReset,
  errorHandlerUpdated,
} from "../../../features/errors/errorSlice"
import { selectContent } from "../../CMS/model"
import { DataStatus } from "../../enums"
import appInsightsLogger from "../../../features/logging/appInsightsLogger"
import { FileStatusDisplayIdEnum } from "../../Files/schema"
import { downloadZip } from "../../Files/helpers"

interface LiabilityInsuranceResponse {
  createdDateTime: Date
  createdBy: string
  expirationDate: Date
  fileId: string
  fileName: string
  fileStatus: FileStatusDisplayIdEnum
  id: string
  insuranceStatus: keyof LiabilityStatusMap
  issueDate: Date
  lastUpdatedDateTime: Date
}
export const fetchLiabilityInsurances = createAsyncThunk(
  "practiceProfile/liabilityInformation/fetchLiabilityInsurances",

  async (practiceId: string, { dispatch, getState }) => {
    try {
      dispatch(
        practiceDataStatusUpdated({
          liabilityInformationDataStatus: DataStatus.LOADING,
        }),
      )
      const response = await axios.get<LiabilityInsuranceResponse[]>(
        getBaseEndpoint() +
          `/practice/practices/${practiceId}/liabilityInsurances`,
      )

      if (response.status === 200) {
        const state = getState() as RootState
        const liabilityInsurances = response.data
        const partialPractice: Partial<Practice> = {
          name: state.practice.practice.name,
          liabilityInsurances: liabilityInsurances,
        }
        const updatedPractice: Practice = cloneDeep({
          ...state.practice.practice,
          ...partialPractice,
        })
        dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
        dispatch(
          practiceDataStatusUpdated({
            liabilityInformationDataStatus: DataStatus.LOADED,
          }),
        )
      } else {
        throw new Error(
          `Failed to liability insurances with status code: ${response.status}`,
        )
      }
    } catch (error) {
      dispatch(
        practiceDataStatusUpdated({
          basePracticeDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)

interface CreateLiabilityInsuranceThunkArgs {
  practiceId: string
  newInsurance: FormData
}
export const postLiabilityInsuranceThunk = createAsyncThunk(
  "practiceProfile/liabilityInformation/createLiabilityInsuranceThunk",
  async (
    { practiceId, newInsurance }: CreateLiabilityInsuranceThunkArgs,
    { dispatch },
  ) => {
    try {
      dispatch(
        practiceDataStatusUpdated({
          liabilityInformationDataStatus: DataStatus.SENDING,
        }),
      )
      const url = `${getBaseEndpoint()}/practice/practices/${practiceId}/liabilityInsurances`
      const response = await axios.post<LiabilityInsuranceResponse>(
        url,
        newInsurance,
      )
      if (response.status === 201) {
        await dispatch(fetchLiabilityInsurances(practiceId))
        return response.data
      } else {
        throw new Error("Failed to create liability information")
      }
    } catch (error) {
      dispatch(
        practiceDataStatusUpdated({
          liabilityInformationDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)

interface PutLiabilityInsuranceArgs {
  practiceId: string
  documentId: string
  dateUpdates: {
    issueDate: Date
    expirationDate: Date
  }
}
export const putLiabilityInsurance = createAsyncThunk(
  "practiceProfile/liabilityInformation/updateLiabilityInsuranceThunk",
  async (
    {
      practiceId: practiceId,
      documentId: insuranceId,
      dateUpdates,
    }: PutLiabilityInsuranceArgs,
    { dispatch },
  ) => {
    try {
      dispatch(
        practiceDataStatusUpdated({
          liabilityInformationDataStatus: DataStatus.SENDING,
        }),
      )
      const url = `${getBaseEndpoint()}/practice/practices/${practiceId}/liabilityInsurances/${insuranceId}`
      const response = await axios.put<LiabilityInsuranceResponse>(
        url,
        dateUpdates,
      )
      if (response.status === 200) {
        dispatch(fetchLiabilityInsurances(practiceId))
      } else {
        throw new Error("Failed to update liability information")
      }
    } catch (error) {
      dispatch(
        practiceDataStatusUpdated({
          liabilityInformationDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)

interface GetLiabilityInsuranceDocumentThunkArgs {
  practiceId: string
  insuranceId: string
  downloadFileNameNoExt?: string
}
export const getLiabilityInsuranceDocumentThunk = createAsyncThunk(
  "practiceProfile/liabilityInformation/getLiabilityInsuranceDocumentThunk",
  async (
    {
      practiceId,
      insuranceId,
      downloadFileNameNoExt,
    }: GetLiabilityInsuranceDocumentThunkArgs,
    { dispatch, getState },
  ) => {
    const rootState = getState() as RootState
    const { common: commonText } = selectContent(rootState)
    try {
      dispatch(bannersReset())
      const url = `${getBaseEndpoint()}/practice/practices/${practiceId}/liabilityInsurances/${insuranceId}/content`
      const response = await axios.get(url, { responseType: "blob" })
      if (response.status === 200) {
        const fileBlob: Blob = response.data
        const contentDisposition = response.headers["content-disposition"]
        const filename =
          downloadFileNameNoExt ||
          getFilenameFromContentDisposition(contentDisposition)

        downloadFileBlob(fileBlob, filename)
      } else if (response.status == 202) {
        dispatch(
          errorHandlerUpdated({
            infoBanner: {
              isVisible: true,
              title: "Warning",
              content: commonText?.fileProcessing || "",
            },
          }),
        )
      } else {
        dispatch(
          errorHandlerUpdated({
            errorBanner: {
              isVisible: true,
              title: "Error",
              content: commonText?.fileDownloadError || "",
            },
          }),
        )
      }
    } catch (error) {
      dispatch(
        practiceDataStatusUpdated({
          liabilityInformationDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
    }
  },
)

interface GetPracticeLiabilityFilesZipArgs {
  practiceId: string | undefined
  downloadFileNameNoExt: string
  liabilityFileData: Array<{
    liabilityInsuranceId: string // insurance id
    fileName: string
  }>
  setDataStatus?: (status: DataStatus) => void
}
export const getPracticeLiabilityFilesZip = createAsyncThunk(
  "practiceProfile/liabilityInformation/getPracticeLiabilityFilesZip",
  async (
    {
      practiceId,
      downloadFileNameNoExt,
      liabilityFileData,
      setDataStatus,
    }: GetPracticeLiabilityFilesZipArgs,
    { dispatch, getState },
  ) => {
    const state = getState() as RootState
    const { common: commonText } = selectContent(state)

    dispatch(bannersReset())

    const practiceLiabilityDownloadInfo = liabilityFileData.map(
      (fileDatum) => ({
        fileName: fileDatum.fileName,
        downloadUrl: `${getBaseEndpoint()}/practice/practices/${practiceId}/liabilityInsurances/${fileDatum.liabilityInsuranceId}/content`,
      }),
    )
    const downloadSuccess = await downloadZip({
      fileDownloadInfo: practiceLiabilityDownloadInfo,
      downloadFileNameNoExt,
      setDataStatus,
    })

    if (!downloadSuccess) {
      dispatch(
        errorHandlerUpdated({
          errorBanner: {
            isVisible: true,
            title: "Error",
            content:
              commonText?.fileDownloadError || "Failed to download files",
          },
        }),
      )
    }
  },
)

interface EmptyResponse {}
interface RemoveLiabilityInsuranceThunkArgs {
  practiceId: string
  insuranceId: string
}
export const removeLiabilityInsuranceThunk = createAsyncThunk(
  "practiceProfile/liabilityInformation/removeLiabilityInsuranceThunk",
  async (
    { practiceId, insuranceId }: RemoveLiabilityInsuranceThunkArgs,
    { dispatch },
  ) => {
    try {
      dispatch(
        practiceDataStatusUpdated({
          liabilityInformationDataStatus: DataStatus.SENDING,
        }),
      )
      const url = `${getBaseEndpoint()}/practice/practices/${practiceId}/liabilityInsurances/${insuranceId}`
      const response = await axios.delete<EmptyResponse>(url)
      if (response.status === 204) {
        dispatch(fetchLiabilityInsurances(practiceId))
      } else {
        throw new Error("Failed to delete liability information")
      }
    } catch (error) {
      dispatch(
        practiceDataStatusUpdated({
          liabilityInformationDataStatus: DataStatus.ERROR,
        }),
      )
      appInsightsLogger.traceError(error)
      throw error
    }
  },
)

export const updatePracticeLiabilityInsurance =
  ({
    liabilityInsurances,
  }: {
    liabilityInsurances: LiabilityInsurance[]
  }): AppThunk =>
  async (dispatch, getState) => {
    const state = getState() as RootState
    const partialPractice: Partial<Practice> = {
      name: state.practice.practice.name,
      liabilityInsurances: liabilityInsurances,
    }
    const updatedPractice: Practice = cloneDeep({
      ...state.practice.practice,
      ...partialPractice,
    })
    dispatch(practiceSlice.actions.practiceUpdated(updatedPractice))
  }

export const deletePracticeLiabilityInsurance =
  (practiceId: string, liabilityInsuranceId: string): AppThunk =>
  async () => {
    try {
      const url = `${getBaseEndpoint()}/practice/practices/${practiceId}/liabilityInsurances/${liabilityInsuranceId}`
      await axios.delete(url)
    } catch (error) {
      appInsightsLogger.traceError(error)
    }
  }
